import { telegramWebApp } from "../telegram/webapp";

export type MeResponse = {
  user: { id: number; username: string | null; firstName: string };
  capabilities: {
    admin: boolean;
    max: boolean;
    globalSettings: boolean;
    richPosts: boolean;
  };
  limits: {
    buttons: { total: number; perRow: number };
  };
};

export type DeliveryPreference = "auto" | "legacy" | "rich";

export type CustomPostAppearance = {
  deliveryPreference: DeliveryPreference;
  layout: "media_first" | "text_first";
  buttonAlignment: "left" | "center" | "right";
  richVideoOverrideAck: boolean;
};

export type UrlButton = {
  text: string;
  url: string;
  row: number;
  style?: "primary" | "success" | "danger";
  customEmojiId?: string;
  emojiFallback?: string;
};

export type PostKind = "custom" | "price";
export type LiveSyncState = "never" | "pending" | "synced" | "failed";
export type TelegramButtonStyle = "default" | "primary" | "success" | "danger";
export type ButtonLayoutRow = {
  id: string;
  buttons: Array<{
    id: string;
    text: string;
    url: string;
    style: TelegramButtonStyle;
    customEmojiId?: string;
    emojiFallback?: string;
  }>;
};

export type PersonalButtonSet = {
  id: string;
  name: string;
  buttonRows: ButtonLayoutRow[];
  createdAt: string;
  updatedAt: string;
};

export type CustomEmojiFormat = "static" | "animated" | "video";

export type PersonalCustomEmoji = {
  customEmojiId: string;
  setName: string | null;
  fallback: string;
  format: CustomEmojiFormat;
  fileId: string;
  fileUniqueId: string;
  thumbnailFileId: string | null;
  needsRepainting: boolean;
  position: number;
  lastUsedAt: string | null;
};

export type PersonalCustomEmojiSet = {
  id: string;
  telegramSetName: string | null;
  title: string;
  thumbnailFileId: string | null;
  emoji: PersonalCustomEmoji[];
  createdAt: string;
  updatedAt: string;
};

export type MiniAppDraft = {
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type MiniAppTemplate = MiniAppDraft & { name: string };

export type AdminCategorySyncRun = {
  id: number;
  ownerUserId: number;
  requestId: string;
  trigger: "manual" | "automatic";
  sourceSlot: string | null;
  state: "pending" | "claimed" | "waiting" | "completed" | "failed";
  targetCount: number;
  queuedCount: number;
  succeededCount: number;
  failedCount: number;
  errorCode: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
};

export type AdminCategorySyncSettings = {
  ownerUserId: number;
  automatic: boolean;
  intervalMinutes: 60;
  nextRunAt: string | null;
  currentRun: AdminCategorySyncRun | null;
};

export type ParsedButtonSet = {
  buttonRows: ButtonLayoutRow[];
  rowCount: number;
  buttonCount: number;
};

export type CreateButtonSetRequest = {
  requestId: string;
  name: string;
  buttonRows: ButtonLayoutRow[];
};

export type UpdateButtonSetRequest = {
  expectedUpdatedAt: string;
  name: string;
  buttonRows: ButtonLayoutRow[];
};
export type PostMediaItem = {
  mediaType: "photo" | "video" | "document";
  mediaFileId: string;
  fileName?: string;
  contentType?: string;
  size?: number;
  width?: number;
  height?: number;
  duration?: number;
};

type EditablePostBase = {
  id: string;
  kind: PostKind;
  chatId: number;
  title: string | null;
  enabled: boolean;
  scheduleType: string;
  intervalMinutes: number | null;
  dailyTime: string | null;
  lastPostAt: string | null;
  trackedMessageIds: number[];
  buttonLinks: UrlButton[];
  buttonRows: ButtonLayoutRow[];
  urlLinkSetId: number | null;
  personalButtonSetId: string | null;
  revision: number;
  publishedRevision: number | null;
  liveSyncState: LiveSyncState;
  liveSyncErrorCode: string | null;
  liveSyncUpdatedAt: string | null;
  liveSyncAppliedCount: number;
  liveSyncTotalCount: number;
  lastPublicationShape: unknown | null;
  mediaItems: PostMediaItem[];
};

export type CustomEditablePost = EditablePostBase & CustomPostAppearance & {
  kind: "custom";
  text: string;
  overflowNoteEnabled: boolean;
  mirrorToMax: boolean;
  deliveryState: string | null;
  deliveryMode: string | null;
  appearance: CustomPostAppearance;
};

export type PriceEditablePost = EditablePostBase & {
  kind: "price";
  categories: string[];
  markupType: "none" | "fixed" | "percent";
  markupValue: number;
  markupRound: boolean;
  markupRoundMode: string | null;
  headerParagraph: string | null;
  contact: string | null;
  extraInfo: string | null;
  promoEnabled: boolean;
  overflowNoteEnabled: boolean;
  postOnChangeOnly: boolean;
  editOnChangeOnly: boolean;
  deliveryMode: "legacy" | "rich";
};

export type EditablePost = CustomEditablePost | PriceEditablePost;

export type CustomPostConfiguration = CustomPostAppearance & {
  chatId: number;
  text: string;
  overflowNoteEnabled: boolean;
  scheduleType: string;
  intervalMinutes: number | null;
  dailyTime: string | null;
  enabled: boolean;
  mediaItems: PostMediaItem[];
  buttonRows: ButtonLayoutRow[];
  mirrorToMax?: boolean;
  urlLinkSetId?: number | null;
  personalButtonSetId?: string | null;
};

export type PricePostConfiguration = {
  chatId: number;
  categories: string[];
  markupType: "none" | "fixed" | "percent";
  markupValue: number;
  markupRound: boolean;
  markupRoundMode: string | null;
  title: string | null;
  headerParagraph: string | null;
  contact: string | null;
  extraInfo: string | null;
  promoEnabled: boolean;
  overflowNoteEnabled: boolean;
  postOnChangeOnly: boolean;
  editOnChangeOnly: boolean;
  scheduleType: string;
  intervalMinutes: number | null;
  dailyTime: string | null;
  enabled: boolean;
  mediaItems: PostMediaItem[];
  buttonRows: ButtonLayoutRow[];
  deliveryMode: "legacy" | "rich";
  urlLinkSetId?: number | null;
  personalButtonSetId?: string | null;
};

export type PostConfiguration = CustomPostConfiguration | PricePostConfiguration;
export type UpdatePostRequest = {
  requestId: string;
  expectedRevision: number;
  previewSessionId: string;
  syncLastPublished: true;
  configuration: PostConfiguration;
};
export type RetryPostSyncRequest = { requestId: string; expectedRevision: number };
export type UpdatePostResponse = EditablePost & { jobId: number | null };
export type ReplacePublishedPostRequest = {
  requestId: string;
  expectedRevision: number;
  confirmReplacement: true;
};
export type PostReplacementState =
  | "pending"
  | "claimed"
  | "delivered"
  | "completed"
  | "failed"
  | "needs_review"
  | "stale";
export type PostReplacementResponse = {
  jobId: number;
  revision: number;
  state: PostReplacementState;
  replayed: boolean;
};
export type PostReplacementStatus = {
  jobId: number;
  revision: number;
  state: PostReplacementState;
  errorCode: string | null;
  updatedAt: string;
};

export type PreviewMessage = {
  kind: "text" | "photo" | "video" | "document" | "media_group" | "links" | "rich";
  text?: string;
  textLimit?: number;
  truncated?: boolean;
  media?: Array<{ index: number; mediaType: "photo" | "video" | "document" }>;
  keyboard?: UrlButton[][];
  showCaptionAboveMedia?: boolean;
  layout?: "media_first" | "text_first";
  buttonAlignment?: "left" | "center" | "right";
};

export type CustomPostPreview = {
  effectiveFormat: "legacy" | "rich";
  overriddenBy: string | null;
  messages: PreviewMessage[];
  warnings: Array<{ code: string; severity: "info" | "warning"; text: string }>;
};

export type PostPreviewResponse = CustomPostPreview & {
  kind: PostKind;
  previewSessionId: string;
};

export type GroupSummary = {
  chatId: number;
  title: string | null;
  type: string | null;
  lastSeenAt?: string | null;
  healthReasonCode?: string | null;
  healthLastFailureAt?: string | null;
  healthLastSuccessfulCheckAt?: string | null;
  customEmojiCapability?: "supported" | "unsupported" | null;
  customEmojiLastRepresentation?: "custom_emoji" | "unicode_fallback" | null;
  customEmojiLastErrorCode?: string | null;
};

export type TargetHealth = {
  chatId: number;
  title: string | null;
  type: string | null;
  reasonCode: string | null;
  lastFailureAt: string | null;
  lastSuccessfulCheckAt: string | null;
};

export type PostSummary = {
  id: string;
  kind: "custom" | "price";
  chatId: number;
  title: string;
  categories?: string[];
  enabled: boolean;
  scheduleType: string;
  dailyTime: string | null;
  intervalMinutes: number | null;
  deliveryState: string | null;
  postOnChangeOnly?: boolean;
  editOnChangeOnly?: boolean;
};

export type DeliverySummary = {
  id: string;
  postKind: "custom" | "price" | "legacy_price";
  postId: string;
  chatId: number;
  scheduleType: string;
  scheduledSlot: string;
  telegramState: "scheduled" | "sending" | "sent" | "failed" | "needs_review";
  telegramMessageIds: number[];
  telegramSentAt: string | null;
  telegramFailedAt: string | null;
  telegramNeedsReviewAt: string | null;
  manualPublishedAt: string | null;
  errorCode: string | null;
  emojiRepresentation?: "custom_emoji" | "unicode_fallback" | "none" | null;
  emojiFallbackCode?: string | null;
  createdAt: string;
  maxRequested: boolean;
  maxState: "not_requested" | "pending" | "sending" | "sent" | "failed" | null;
  maxMessageRef: string | null;
  maxStartedAt: string | null;
  maxSentAt: string | null;
  maxFailedAt: string | null;
};

export type PriceSource = {
  items: Array<{
    categoryKey: string;
    categoryLabel: string;
    name: string;
    priceRaw: string;
    countryCode: string | null;
  }>;
  categories: Array<{ key: string; label: string }>;
};

export type UrlLinkSet = {
  id: number;
  name: string;
  links: UrlButton[];
};

export type UploadedMedia = {
  mediaType: "photo" | "video" | "document";
  mediaFileId: string;
  fileName: string;
  contentType: string;
  size: number;
  width?: number;
  height?: number;
  duration?: number;
  localPreviewUrl?: string;
};

type LocalVideoMetadata = {
  width?: number;
  height?: number;
  duration?: number;
};

const positiveInteger = (value: number, max: number, round: "nearest" | "up") => {
  const normalized = round === "up" ? Math.ceil(value) : Math.round(value);
  return Number.isSafeInteger(normalized) && normalized > 0 && normalized <= max
    ? normalized
    : undefined;
};

const readLocalVideoMetadata = async (file: File): Promise<LocalVideoMetadata> => {
  if (
    !file.type.startsWith("video/") ||
    typeof document === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    return {};
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise<LocalVideoMetadata>((resolve) => {
      const video = document.createElement("video");
      let settled = false;
      const finish = (value: LocalVideoMetadata) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        video.removeAttribute("src");
        resolve(value);
      };
      const timer = window.setTimeout(() => finish({}), 4_000);
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.addEventListener("loadedmetadata", () => {
        const width = positiveInteger(video.videoWidth, 10_000, "nearest");
        const height = positiveInteger(video.videoHeight, 10_000, "nearest");
        const duration = positiveInteger(video.duration, 86_400, "up");
        finish({
          ...(width !== undefined ? { width } : {}),
          ...(height !== undefined ? { height } : {}),
          ...(duration !== undefined ? { duration } : {})
        });
      }, { once: true });
      video.addEventListener("error", () => finish({}), { once: true });
      video.src = objectUrl;
      try {
        video.load();
      } catch {
        finish({});
      }
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

// Лимиты Telegram Bot API на загрузку файла ботом: фото до 10 МБ, остальные файлы до 50 МБ.
// Совпадают с проверками /api/media на сервере.
export const MAX_UPLOAD_BYTES = 50_000_000;
export const MAX_PHOTO_UPLOAD_BYTES = 10_000_000;

const PHOTO_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const uploadMediaType = (file: File): UploadedMedia["mediaType"] =>
  PHOTO_CONTENT_TYPES.includes(file.type)
    ? "photo"
    : file.type.startsWith("video/")
      ? "video"
      : "document";

const formatDecimalMegabytes = (size: number) =>
  (Math.ceil(size / 100_000) / 10).toLocaleString("ru-RU");

export const oversizedUploadMessage = (file: File) => {
  const weight = `«${file.name || "Файл"}» весит ${formatDecimalMegabytes(file.size)} МБ`;
  const mediaType = uploadMediaType(file);
  if (mediaType === "photo") {
    return `${weight}, а Telegram принимает фото до 10 МБ. Уменьшите или сожмите фото и загрузите снова.`;
  }
  return `${weight}, а Telegram принимает от ботов файлы до 50 МБ. ` +
    (mediaType === "video"
      ? "Сожмите или обрежьте видео и загрузите снова."
      : "Уменьшите файл и загрузите снова.");
};

// Причина, по которой файл нельзя загрузить, или null.
export const uploadRejection = (file: File) => {
  if (file.size === 0) return `«${file.name || "Файл"}» пустой (0 байт). Выберите другой файл.`;
  const limit = uploadMediaType(file) === "photo" ? MAX_PHOTO_UPLOAD_BYTES : MAX_UPLOAD_BYTES;
  return file.size > limit ? oversizedUploadMessage(file) : null;
};

export const firstUploadRejection = (files: readonly File[]) => {
  for (const file of files) {
    const rejection = uploadRejection(file);
    if (rejection) return rejection;
  }
  return null;
};

export const SESSION_EXPIRED_MESSAGE =
  "Сессия Mini App устарела. Закройте Mini App и снова откройте его из бота.";

export const isSessionExpiredError = (error: unknown) => {
  const code = (error as { code?: unknown } | null)?.code;
  return code === "INIT_DATA_EXPIRED" || code === "UNAUTHORIZED";
};

// Технические ответы сервера и nginx заменяем понятным текстом;
// русские сообщения бизнес-логики сервера показываем как есть.
const apiErrorMessage = (status: number, code: string, message: string | undefined) => {
  if (code === "INIT_DATA_EXPIRED" || code === "UNAUTHORIZED") return SESSION_EXPIRED_MESSAGE;
  if (code === "PAYLOAD_TOO_LARGE" || (status === 413 && code === "REQUEST_FAILED")) {
    return "Файл слишком большой: Telegram принимает от ботов файлы до 50 МБ.";
  }
  if (code === "RATE_LIMITED" || status === 429) {
    return "Слишком много запросов подряд. Подождите минуту и повторите.";
  }
  if (status >= 500 && (code === "INTERNAL_ERROR" || code === "REQUEST_FAILED")) {
    return "Сервер не смог выполнить запрос. Попробуйте ещё раз через минуту.";
  }
  return message ?? "Запрос не выполнен";
};

const toApiError = (status: number, body: unknown) => {
  const error = (body as { error?: { code?: unknown; message?: unknown } } | null)?.error;
  const code = typeof error?.code === "string" ? error.code : "REQUEST_FAILED";
  const message = typeof error?.message === "string" ? error.message : undefined;
  return new ApiError(status, code, apiErrorMessage(status, code, message));
};

export const apiRequest = async <T>(
  path: string,
  init: RequestInit = {}
): Promise<T> => {
  const initData = telegramWebApp()?.initData ?? "";
  const response = await fetch(path, {
    ...init,
    headers: {
      authorization: `tma ${initData}`,
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...(init.headers ?? {})
    }
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw toApiError(response.status, body);
  }
  return body as T;
};

const apiBlobRequest = async (path: string, init: RequestInit = {}) => {
  const initData = telegramWebApp()?.initData ?? "";
  const response = await fetch(path, {
    ...init,
    headers: {
      authorization: `tma ${initData}`,
      ...(init.headers ?? {})
    }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw toApiError(response.status, body);
  }
  return response.blob();
};

export const defaultApi = {
  loadMe: () => apiRequest<MeResponse>("/api/me"),
  loadGroups: async () =>
    (await apiRequest<{ items: GroupSummary[] }>("/api/groups")).items,
  loadGroupHealth: async () =>
    (await apiRequest<{ items: TargetHealth[] }>("/api/groups/health")).items,
  checkGroup: (chatId: number) =>
    apiRequest<{ item: TargetHealth; requiresEnable: boolean }>(
      `/api/groups/${chatId}/check`,
      { method: "POST", body: "{}" }
    ),
  enableGroup: (chatId: number) =>
    apiRequest<{ item: TargetHealth; reenabled: boolean }>(
      `/api/groups/${chatId}/enable`,
      { method: "POST", body: "{}" }
    ),
  loadPosts: async (signal?: AbortSignal) =>
    (await apiRequest<{ items: PostSummary[] }>("/api/posts", { signal })).items,
  loadPost: (kind: PostKind, id: string, signal?: AbortSignal) =>
    apiRequest<EditablePost>(`/api/posts/${kind}/${id}`, { signal }),
  loadPostMedia: (kind: PostKind, id: string, index: number, signal?: AbortSignal) =>
    apiBlobRequest(`/api/posts/${kind}/${id}/media/${index}`, { signal }),
  updatePost: (
    kind: PostKind,
    id: string,
    body: UpdatePostRequest,
    signal?: AbortSignal
  ) => apiRequest<UpdatePostResponse>(`/api/posts/${kind}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
    signal
  }),
  retryPostSync: (
    kind: PostKind,
    id: string,
    body: RetryPostSyncRequest,
    signal?: AbortSignal
  ) => apiRequest<UpdatePostResponse>(`/api/posts/${kind}/${id}/sync-last`, {
    method: "POST",
    body: JSON.stringify(body),
    signal
  }),
  replacePublishedPost: (
    kind: PostKind,
    id: string,
    body: ReplacePublishedPostRequest,
    signal?: AbortSignal
  ) => apiRequest<PostReplacementResponse>(
    `/api/posts/${kind}/${id}/replace-published`,
    { method: "POST", body: JSON.stringify(body), signal }
  ),
  loadReplacementStatus: (
    kind: PostKind,
    id: string,
    jobId: number,
    signal?: AbortSignal
  ) => apiRequest<PostReplacementStatus>(
    `/api/posts/${kind}/${id}/replacements/${jobId}`,
    { signal }
  ),
  deletePost: async (kind: PostKind, id: string) => {
    await apiRequest<unknown>(`/api/posts/${kind}/${id}`, { method: "DELETE" });
  },
  loadDeliveries: async (signal?: AbortSignal) =>
    (await apiRequest<{ items: DeliverySummary[] }>("/api/deliveries", { signal })).items,
  retryMaxDelivery: (
    id: string,
    requestId: string,
    signal?: AbortSignal
  ) =>
    apiRequest<Record<string, unknown>>(`/api/deliveries/${id}/retry-max`, {
      method: "POST",
      body: JSON.stringify({ requestId }),
      signal
    }),
  markDeliveryPublished: (id: string, signal?: AbortSignal) =>
    apiRequest<DeliverySummary>(`/api/deliveries/${id}/mark-published`, {
      method: "POST",
      body: "{}",
      signal
    }),
  rescheduleDelivery: (
    id: string,
    dailyTime: string,
    requestId: string,
    signal?: AbortSignal
  ) =>
    apiRequest<Record<string, unknown>>(`/api/deliveries/${id}/reschedule`, {
      method: "POST",
      body: JSON.stringify({ dailyTime, requestId }),
      signal
    }),
  loadPrice: (signal?: AbortSignal) => apiRequest<PriceSource>("/api/price", { signal }),
  loadDrafts: async () =>
    (await apiRequest<{ items: MiniAppDraft[] }>("/api/drafts")).items,
  loadTemplates: async () =>
    (await apiRequest<{ items: MiniAppTemplate[] }>("/api/templates")).items,
  loadButtonSets: async () =>
    (await apiRequest<{ items: PersonalButtonSet[] }>("/api/button-sets")).items,
  loadCustomEmojiSets: async () =>
    (await apiRequest<{ items: PersonalCustomEmojiSet[] }>("/api/custom-emoji/sets")).items,
  loadRecentCustomEmoji: async () =>
    (await apiRequest<{ items: PersonalCustomEmoji[] }>("/api/custom-emoji/recent")).items,
  markCustomEmojiUsed: (customEmojiId: string) =>
    apiRequest<PersonalCustomEmoji>(
      `/api/custom-emoji/${encodeURIComponent(customEmojiId)}/use`,
      { method: "POST", body: "{}" }
    ),
  loadCustomEmojiPreview: (customEmojiId: string, signal?: AbortSignal) =>
    apiBlobRequest(
      `/api/custom-emoji/${encodeURIComponent(customEmojiId)}/preview?v=2`,
      { signal }
    ),
  parseButtonSet: (source: string) =>
    apiRequest<ParsedButtonSet>("/api/button-sets/parse", {
      method: "POST",
      body: JSON.stringify({ source })
    }),
  createButtonSet: (body: CreateButtonSetRequest) =>
    apiRequest<PersonalButtonSet>("/api/button-sets", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  updateButtonSet: (id: string, body: UpdateButtonSetRequest) =>
    apiRequest<PersonalButtonSet>(`/api/button-sets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
  copyButtonSet: (id: string, body: { requestId: string; name: string }) =>
    apiRequest<PersonalButtonSet>(`/api/button-sets/${id}/copy`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  deleteButtonSet: async (id: string) => {
    await apiRequest<unknown>(`/api/button-sets/${id}`, { method: "DELETE" });
  },
  loadUrlLinkSets: async () =>
    (await apiRequest<{ items: UrlLinkSet[] }>("/api/admin/url-link-sets")).items,
  loadCategorySync: () =>
    apiRequest<AdminCategorySyncSettings>("/api/admin/category-sync"),
  configureCategorySync: (automatic: boolean) =>
    apiRequest<AdminCategorySyncSettings>("/api/admin/category-sync", {
      method: "PATCH",
      body: JSON.stringify({ automatic })
    }),
  runCategorySync: (requestId: string) =>
    apiRequest<{ replayed: boolean; run: AdminCategorySyncRun }>(
      "/api/admin/category-sync/run",
      { method: "POST", body: JSON.stringify({ requestId }) }
    ),
  uploadMedia: async (file: File) => {
    const rejection = uploadRejection(file);
    if (rejection) {
      throw file.size === 0
        ? new ApiError(400, "INVALID_INPUT", rejection)
        : new ApiError(413, "FILE_TOO_LARGE", rejection);
    }
    const mediaType = uploadMediaType(file);
    const metadata = mediaType === "video" ? await readLocalVideoMetadata(file) : {};
    return apiRequest<UploadedMedia>("/api/media", {
      method: "POST",
      body: file,
      headers: {
        "content-type": file.type || "application/octet-stream",
        "x-file-name": encodeURIComponent(file.name || "media"),
        "x-media-type": mediaType,
        ...(metadata.width !== undefined ? { "x-media-width": String(metadata.width) } : {}),
        ...(metadata.height !== undefined ? { "x-media-height": String(metadata.height) } : {}),
        ...(metadata.duration !== undefined ? { "x-media-duration": String(metadata.duration) } : {})
      }
    });
  },
  preview: (body: Record<string, unknown>) =>
    apiRequest<PostPreviewResponse>("/api/preview", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  createPost: (body: Record<string, unknown>) =>
    apiRequest<Record<string, unknown>>("/api/posts", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  saveDraft: (body: Record<string, unknown>) =>
    apiRequest<Record<string, unknown>>("/api/drafts", {
      method: "POST",
      body: JSON.stringify(body)
    })
};
